import React from 'react'
import { useTranslation } from 'react-i18next'
import { GUIDE_START_DATE } from '../constants'
import { State } from '../store'
import { useSelector } from 'react-redux'
import { selectSessionUsers } from '../selectors/sessions'
import { selectMembersWithAccess } from '../selectors/organizations'
import { Box, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { ListItemLocation } from './ListItemLocation'
import { GuideBubble } from './GuideBubble'
import { Icon } from './Icon'

type Props = {
  instance?: IInstance
  service?: IService
  menuItem?: boolean
  to: string
  size?: 'large' | 'small'
}

export const UsersTab: React.FC<Props> = ({ instance, service, menuItem, to, size = 'large' }) => {
  const { t } = useTranslation()
  const connected = useSelector((state: State) =>
    selectSessionUsers(state, undefined, service ? service.id : instance?.id)
  ).length
  const access = useSelector((state: State) => selectMembersWithAccess(state, undefined, instance)).map(m => m.user)
  const users = (service ? service.access : instance?.access) || []
  const usersLinked = access.filter(user => !users.find(_u => _u.email === user.email))
  const total = users.length + usersLinked.length

  if (!instance?.permissions.includes('MANAGE')) return null

  return (
    <GuideBubble
      highlight
      guide="users"
      enterDelay={400}
      placement="bottom"
      startDate={GUIDE_START_DATE}
      added={GUIDE_START_DATE}
      queueAfter="addService"
      instructions={
        <>
          <Typography variant="h3" gutterBottom>
            <b>{t('usersTab.guideTitle', 'The power of sharing')}</b>
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t(
              'usersTab.guideBodyBefore',
              'Sharing can be done directly to a guest user from here, or by adding members to your'
            )}{' '}
            <cite>{t('usersTab.guideOrganization', 'Organization')}</cite>
            {t('usersTab.guideBodyAfter', '.')}
          </Typography>
        </>
      }
    >
      <ListItemLocation to={to} menuItem={menuItem} dense>
        <ListItemIcon>
          <Icon name="user-group" color={connected ? 'primary' : 'grayDarker'} size="md" />
        </ListItemIcon>
        <ListItemText
          sx={{ color: connected ? 'primary.main' : undefined }}
          primary={t('usersTab.access', 'Access')}
          secondary={
            !!total &&
            size === 'large' && (
              <>
                {total ? t('usersTab.totalCount', { count: total, defaultValue: '{{count}} total' }) : ''}
                <br />
                {!!connected && (
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    {t('usersTab.connectedCount', { count: connected, defaultValue: '{{count}} connected' })}
                  </Box>
                )}
              </>
            )
          }
        />
      </ListItemLocation>
    </GuideBubble>
  )
}
